/**
 * Global navigation data — mega-menu structure for the entire site.
 * Every link points to a real, handcrafted page.
 * URLs mirror the content/ folder structure 1:1 via each .md's slug field.
 */

export interface NavItem {
  label: string;
  href: string;
  icon: string;
  description?: string;
}

export interface NavColumn {
  title: string;
  items: NavItem[];
}

export interface NavGroup {
  label: string;
  href: string;
  icon: string;
  columns: NavColumn[];
  featured?: {
    title: string;
    description: string;
    href: string;
    cta: string;
    accent: string;
  };
}

export const NAV_GROUPS: NavGroup[] = [
  {
    label: "Platform",
    href: "/platform",
    icon: "LayoutDashboard",
    columns: [
      {
        title: "Core",
        items: [
          { label: "Platform Hub", href: "/platform", icon: "LayoutDashboard", description: "All platform pages" },
          { label: "Overview", href: "/platform/overview", icon: "Eye", description: "Platform at a glance" },
          { label: "How It Works", href: "/how-it-works", icon: "Workflow", description: "End-to-end process" },
          { label: "Capabilities", href: "/platform/capabilities", icon: "Sparkles", description: "20 core capabilities" },
        ],
      },
      {
        title: "Architecture",
        items: [
          { label: "Features", href: "/platform/features", icon: "Boxes", description: "All capabilities" },
          { label: "Architecture", href: "/platform/architecture", icon: "Layers", description: "System design" },
          { label: "Performance", href: "/platform/performance", icon: "Gauge", description: "Speed + scale" },
          { label: "Security", href: "/platform-security", icon: "Shield", description: "Zero-incident track record" },
        ],
      },
      {
        title: "Operations",
        items: [
          { label: "Dashboard", href: "/platform-dashboard", icon: "LayoutDashboard", description: "Live operational view" },
          { label: "Deployment", href: "/platform/deployment", icon: "Rocket", description: "4 deployment tiers" },
          { label: "API", href: "/platform/api", icon: "Code2", description: "REST + SDK surface" },
          { label: "Integrations", href: "/platform/integrations", icon: "Plug", description: "Connect your stack" },
        ],
      },
    ],
    featured: {
      title: "The Perception X2 Platform",
      description: "9 proprietary AI engines, 50+ platforms, 95–98% organic authenticity.",
      href: "/platform",
      cta: "Browse hub",
      accent: "var(--color-crimson)",
    },
  },
  {
    label: "Capabilities",
    href: "/capabilities",
    icon: "Sparkles",
    columns: [
      {
        title: "Content & Narrative",
        items: [
          { label: "Capabilities Hub", href: "/capabilities", icon: "LayoutGrid", description: "All 20 capabilities" },
          { label: "Narrative Engineering", href: "/capabilities/narrative-engineering", icon: "BookOpen", description: "Strategic backbone" },
          { label: "Content Generation", href: "/content-generation", icon: "FileText", description: "100K–10M+ variants/day" },
          { label: "Content Optimization", href: "/capabilities/content-optimization", icon: "Sliders", description: "Continuous refinement" },
          { label: "Message Architecture", href: "/message-architecture", icon: "MessageSquare", description: "Story frameworks" },
          { label: "Story Framework", href: "/story-framework", icon: "BookText", description: "Narrative systems" },
        ],
      },
      {
        title: "Distribution",
        items: [
          { label: "Multi-Platform Deployment", href: "/capabilities/multi-platform-deployment", icon: "Send", description: "50+ platforms coordinated" },
          { label: "Organic Amplification", href: "/organic-amplification", icon: "TrendingUp", description: "300–500% reach gain" },
          { label: "Influencer Orchestration", href: "/influencer-orchestration", icon: "Users", description: "Tier-based coordination" },
          { label: "Asset Management", href: "/asset-creation-management", icon: "Package", description: "Lifecycle + governance" },
        ],
      },
      {
        title: "Intelligence & Defense",
        items: [
          { label: "Crisis Response", href: "/crisis-narrative-response", icon: "Siren", description: "384×–1,416× faster" },
          { label: "Emotional Resonance", href: "/capabilities/emotional-resonance", icon: "Heart", description: "144-D mapping" },
          { label: "Audience Targeting", href: "/capabilities/audience-targeting", icon: "Target", description: "200+ bias mappings" },
          { label: "Sentiment Analysis", href: "/sentiment-analysis", icon: "Activity", description: "Sub-second processing" },
          { label: "Reputation Management", href: "/reputation-management", icon: "Award", description: "Cross-platform trust" },
        ],
      },
      {
        title: "Culture & Influence",
        items: [
          { label: "Cultural Localization", href: "/capabilities/cultural-localization", icon: "Globe", description: "85+ regions, 15+ languages" },
          { label: "Cognitive Bias", href: "/capabilities/cognitive-bias", icon: "Brain", description: "Psychological levers" },
          { label: "Automated Influence", href: "/capabilities/automated-influence", icon: "Bot", description: "Autonomous agents" },
          { label: "Meme & Culture", href: "/capabilities/meme-culture", icon: "Smile", description: "Cultural velocity" },
          { label: "Trend Seeding", href: "/capabilities/trend-seeding", icon: "Flame", description: "Emerging narratives" },
          { label: "Viral Injection", href: "/capabilities/viral-narrative-injection", icon: "Zap", description: "Propagation engineering" },
        ],
      },
    ],
    featured: {
      title: "Twenty Core Capabilities",
      description: "Integrated, autonomous, operating in concert — 300–500% narrative penetration.",
      href: "/capabilities",
      cta: "Browse hub",
      accent: "var(--color-purple)",
    },
  },
  {
    label: "Engines",
    href: "/engines",
    icon: "Cpu",
    columns: [
      {
        title: "Proprietary AI",
        items: [
          { label: "Engines Hub", href: "/engines", icon: "LayoutGrid", description: "All 5 engines" },
          { label: "OSE — Organic Simulation", href: "/organic-simulation-engine", icon: "Activity", description: "Authenticity at scale" },
          { label: "NGE — Narrative Generation", href: "/engines/narrative-generation", icon: "PenTool", description: "Story synthesis" },
          { label: "CRE — Cognitive Resonance", href: "/cognitive-resonance", icon: "Brain", description: "144-D emotional mapping" },
        ],
      },
      {
        title: "Autonomy",
        items: [
          { label: "ECA — Echo Chamber", href: "/engines/echo-chamber", icon: "Radio", description: "Amplification mesh" },
          { label: "AOE — Autonomous Operations", href: "/autonomous-operations", icon: "Bot", description: "Self-optimizing execution" },
        ],
      },
    ],
    featured: {
      title: "5 Engines, One Platform",
      description: "Each purpose-built. Together, they deliver autonomous amplification.",
      href: "/engines",
      cta: "Browse engines",
      accent: "var(--color-cyan)",
    },
  },
  {
    label: "Services",
    href: "/services",
    icon: "Briefcase",
    columns: [
      {
        title: "Hub + Top Services",
        items: [
          { label: "Services Hub", href: "/services", icon: "LayoutGrid", description: "All service categories" },
          { label: "Competitive Intelligence", href: "/competitive-intelligence", icon: "Telescope", description: "Outpace rivals" },
          { label: "Reputation Management", href: "/reputation-management", icon: "Award", description: "Sustained trust" },
        ],
      },
      {
        title: "Brand & Content",
        items: [
          { label: "Brand Protection Hub", href: "/services/brand-protection", icon: "Shield", description: "Defend equity" },
          { label: "Brand Protection", href: "/brand-protection-augmentation", icon: "Shield", description: "Augmented" },
          { label: "Brand Augmentation", href: "/brand-augmentation", icon: "Sparkles", description: "Strengthen presence" },
          { label: "Competitor Analysis", href: "/competitor-analysis", icon: "Crosshair", description: "Strategic positioning" },
          { label: "Content Engineering Hub", href: "/services/content-engineering", icon: "PenTool", description: "Strategic content systems" },
          { label: "Content Creation", href: "/content-narrative-engineering/content-creation", icon: "FileText", description: "Production at scale" },
          { label: "Buzz Marketing", href: "/buzz-marketing", icon: "Megaphone", description: "Conversation ignition" },
          { label: "Mass Communication", href: "/mass-communication", icon: "Radio", description: "Wide-reach deployment" },
        ],
      },
      {
        title: "Crisis & Defense",
        items: [
          { label: "Crisis Management Hub", href: "/services/crisis-management", icon: "Siren", description: "All crisis services" },
          { label: "Crisis Management", href: "/crisis-management-response", icon: "Siren", description: "Rapid response" },
          { label: "Crisis Prevention", href: "/crisis-prevention", icon: "ShieldCheck", description: "Pre-positioned defense" },
          { label: "Crisis Response", href: "/crisis-response", icon: "Activity", description: "Containment protocols" },
          { label: "Threat Analysis", href: "/threat-analysis", icon: "AlertTriangle", description: "Early detection" },
          { label: "Digital Intelligence Hub", href: "/services/digital-intelligence", icon: "Radar", description: "Monitor + listen" },
        ],
      },
      {
        title: "Recovery & Relations",
        items: [
          { label: "Reputation Mgmt Hub", href: "/reputation-management", icon: "Award", description: "Subdirectory hub" },
          { label: "Reputation Analysis", href: "/reputation-analysis", icon: "LineChart", description: "Diagnostic depth" },
          { label: "Review Management", href: "/review-management", icon: "Star", description: "Cross-platform trust" },
          { label: "Review Optimization", href: "/review-optimization", icon: "BadgeCheck", description: "Sentiment calibration" },
          { label: "Damage Control", href: "/damage-control", icon: "Shield", description: "Containment + recovery" },
          { label: "Online Reputation Monitoring", href: "/online-reputation-monitoring", icon: "Eye", description: "Continuous surveillance" },
          { label: "Media Relations Hub", href: "/services/media-relations", icon: "Newspaper", description: "PR + comms" },
          { label: "Influencer Hub", href: "/influencer-orchestration", icon: "Users", description: "Tier-based" },
          { label: "Search Dominance Hub", href: "/services/search-dominance", icon: "Search", description: "SEO + SERP" },
        ],
      },
    ],
    featured: {
      title: "Integrated Perception Services",
      description: "Reputation, content, crisis, intelligence — coordinated across 50+ platforms.",
      href: "/services",
      cta: "Browse hub",
      accent: "var(--color-emerald)",
    },
  },
  {
    label: "Use Cases",
    href: "/use-cases/enterprises",
    icon: "Target",
    columns: [
      {
        title: "Enterprise",
        items: [
          { label: "Enterprises", href: "/use-cases/enterprises", icon: "Building2", description: "Fortune-class brands" },
          { label: "Financial Services", href: "/financial-services", icon: "TrendingUp", description: "Banking + markets" },
          { label: "Healthcare", href: "/use-cases/healthcare", icon: "HeartPulse", description: "Trust-critical" },
          { label: "Technology", href: "/technology", icon: "Cpu", description: "Tech + SaaS" },
        ],
      },
      {
        title: "Public Sector",
        items: [
          { label: "Public Figures", href: "/use-cases/public-figures", icon: "User", description: "High-profile individuals" },
          { label: "Political Campaigns", href: "/political-campaigns", icon: "Vote", description: "Electoral strategy" },
          { label: "Defense", href: "/use-cases/defense", icon: "Shield", description: "Sovereign perception" },
          { label: "Legal", href: "/legal", icon: "Scale", description: "Legal-sector perception" },
        ],
      },
      {
        title: "Other Verticals",
        items: [
          { label: "Education", href: "/use-cases/education", icon: "GraduationCap", description: "Institutional trust" },
          { label: "Media & Entertainment", href: "/media-entertainment", icon: "Film", description: "Audience narratives" },
          { label: "Non-Profits", href: "/use-cases/non-profits", icon: "HandHeart", description: "Mission-aligned perception" },
        ],
      },
    ],
    featured: {
      title: "Industry deep-dives",
      description: "Sector-specific playbooks, KPIs, and documented outcomes.",
      href: "/platform/overview",
      cta: "View outcomes",
      accent: "var(--color-amber)",
    },
  },
  {
    label: "Technology",
    href: "/technology",
    icon: "Layers",
    columns: [
      {
        title: "AI & Intelligence",
        items: [
          { label: "Technology Hub", href: "/technology", icon: "LayoutGrid", description: "All 10 surfaces" },
          { label: "AI & Machine Learning", href: "/technology/ai-ml", icon: "Brain", description: "Core AI capability" },
          { label: "Natural Language Processing", href: "/technology/nlp", icon: "MessageSquare", description: "Language understanding" },
          { label: "Computer Vision", href: "/technology/computer-vision", icon: "Eye", description: "Visual intelligence" },
          { label: "Predictive Analytics", href: "/technology/predictive-analytics", icon: "Telescope", description: "Trajectory forecasting" },
        ],
      },
      {
        title: "Infrastructure",
        items: [
          { label: "Data Architecture", href: "/technology/data", icon: "Database", description: "Pipeline design" },
          { label: "Infrastructure", href: "/technology/infrastructure", icon: "Server", description: "Global deployment" },
          { label: "Real-Time Processing", href: "/technology/real-time-processing", icon: "Zap", description: "Sub-second pipelines" },
          { label: "Reliability", href: "/reliability", icon: "ShieldCheck", description: "99.9999% uptime" },
          { label: "Scalability", href: "/scalability", icon: "Maximize", description: "Elastic architecture" },
          { label: "Security Architecture", href: "/security-architecture", icon: "Shield", description: "Defense-grade" },
        ],
      },
    ],
    featured: {
      title: "Proprietary Technology Stack",
      description: "9 AI engines. 144 dimensions. Zero third-party dependencies.",
      href: "/technology",
      cta: "Browse hub",
      accent: "var(--color-denim)",
    },
  },
  {
    label: "Analytics",
    href: "/analytics",
    icon: "BarChart3",
    columns: [
      {
        title: "Operational Analytics",
        items: [
          { label: "Analytics Hub", href: "/analytics", icon: "LayoutGrid", description: "All 8 surfaces" },
          { label: "Dashboards", href: "/analytics/dashboards", icon: "LayoutDashboard", description: "Live operational view" },
          { label: "Performance Metrics", href: "/analytics-performance-metrics", icon: "Gauge", description: "Documented KPIs" },
          { label: "Engagement", href: "/analytics/engagement", icon: "Activity", description: "Interaction analysis" },
          { label: "Comparative", href: "/analytics/comparative", icon: "GitCompare", description: "Benchmark + contrast" },
        ],
      },
      {
        title: "Insight",
        items: [
          { label: "Predictive Analytics", href: "/predictive-analytics", icon: "Telescope", description: "Forecasting models" },
          { label: "Sentiment", href: "/analytics/sentiment", icon: "Heart", description: "Real-time sentiment" },
          { label: "Conversion", href: "/analytics/conversion", icon: "Target", description: "Outcome attribution" },
          { label: "Custom Reports", href: "/custom-reports", icon: "FileBarChart", description: "Tailored reporting" },
        ],
      },
    ],
    featured: {
      title: "144-Dimensional Analytics",
      description: "Real-time, autonomous, executive-level visibility.",
      href: "/analytics",
      cta: "Browse hub",
      accent: "var(--color-violet)",
    },
  },
  {
    label: "Autonomous",
    href: "/autonomous",
    icon: "Bot",
    columns: [
      {
        title: "Adaptive Systems",
        items: [
          { label: "Autonomous Hub", href: "/autonomous", icon: "LayoutGrid", description: "All 8 surfaces" },
          { label: "Adaptive Response", href: "/autonomous/adaptive-response", icon: "RefreshCw", description: "Real-time adjustment" },
          { label: "Decision Triggers", href: "/automated-decision-triggers", icon: "Zap", description: "Threshold activation" },
          { label: "Persona Networks", href: "/autonomous/persona-networks", icon: "Network", description: "10K–1M+ persona modeling" },
        ],
      },
      {
        title: "Optimization",
        items: [
          { label: "Performance Maximization", href: "/performance-maximization", icon: "TrendingUp", description: "Continuous lift" },
          { label: "Budget Optimization", href: "/autonomous/budget-optimization", icon: "CircleDollarSign", description: "Spend allocation" },
          { label: "Content Factory", href: "/content-factory", icon: "Factory", description: "Production automation" },
          { label: "Sentiment Mesh", href: "/autonomous/sentiment-mesh", icon: "Share2", description: "Distributed monitoring" },
          { label: "Monitoring", href: "/monitoring", icon: "Eye", description: "Always-on awareness" },
        ],
      },
    ],
    featured: {
      title: "Autonomous Operations",
      description: "Systems that perceive, decide, and amplify — without manual intervention.",
      href: "/autonomous",
      cta: "Browse hub",
      accent: "var(--color-emerald)",
    },
  },
  {
    label: "Resources",
    href: "/resources",
    icon: "Library",
    columns: [
      {
        title: "Hub + Learn",
        items: [
          { label: "Resources Hub", href: "/resources", icon: "LayoutGrid", description: "All 17 resources" },
        ],
      },
      {
        title: "Documentation",
        items: [
          { label: "Documentation", href: "/documentation", icon: "BookText", description: "Platform reference" },
          { label: "Guides", href: "/guides", icon: "BookOpen", description: "Step-by-step playbooks" },
          { label: "Best Practices", href: "/best-practices", icon: "Trophy", description: "Proven patterns" },
          { label: "Glossary", href: "/glossary", icon: "BookText", description: "Industry terminology" },
        ],
      },
      {
        title: "Research",
        items: [
          { label: "Whitepapers", href: "/whitepapers", icon: "FileText", description: "Deep-dive research" },
          { label: "Technical Papers", href: "/technical-papers", icon: "Cpu", description: "AI + systems papers" },
          { label: "Security Whitepaper", href: "/security-whitepaper", icon: "Shield", description: "Defense posture" },
          { label: "Industry Reports", href: "/industry-reports", icon: "FileBarChart", description: "Sector analysis" },
          { label: "Case Studies", href: "/case-studies", icon: "Briefcase", description: "Documented outcomes" },
        ],
      },
      {
        title: "Media & Community",
        items: [
          { label: "Blog", href: "/blog", icon: "Newspaper", description: "Latest insights" },
          { label: "Newsletter", href: "/newsletter", icon: "Mail", description: "Subscribe" },
          { label: "Webinars", href: "/webinars", icon: "Video", description: "Live sessions" },
          { label: "Videos", href: "/resources/videos", icon: "Film", description: "On-demand library" },
          { label: "AI Ethics", href: "/ai-ethics", icon: "Scale", description: "Our principles" },
          { label: "Compliance", href: "/compliance-guide", icon: "FileCheck", description: "Regulatory" },
          { label: "Migration", href: "/migration", icon: "ArrowRightLeft", description: "Switching guide" },
          { label: "FAQ", href: "/faq", icon: "HelpCircle", description: "Common questions" },
        ],
      },
    ],
    featured: {
      title: "Knowledge Library",
      description: "Whitepapers, guides, case studies, and on-demand sessions.",
      href: "/documentation",
      cta: "Browse library",
      accent: "var(--color-amber)",
    },
  },
  {
    label: "Developers",
    href: "/developers/portal",
    icon: "Terminal",
    columns: [
      {
        title: "Build",
        items: [
          { label: "Developer Portal", href: "/developers/portal", icon: "LayoutDashboard", description: "Central hub" },
          { label: "API Documentation", href: "/developers/api-documentation", icon: "BookText", description: "REST + SDK reference" },
          { label: "SDK", href: "/developers/sdk", icon: "Package", description: "Client libraries" },
          { label: "Webhooks", href: "/developers/webhooks", icon: "Bell", description: "Event subscriptions" },
        ],
      },
      {
        title: "Integrate",
        items: [
          { label: "Integration Guides", href: "/developers/integration-guides", icon: "BookOpen", description: "Step-by-step" },
          { label: "Code Examples", href: "/developers/code-examples", icon: "Code2", description: "Reference patterns" },
          { label: "Community", href: "/developers/community", icon: "Users", description: "Developer forum" },
        ],
      },
    ],
    featured: {
      title: "Build with Perception X2",
      description: "9 engines, 50+ platforms, 144 dimensions. Documented, testable, fast.",
      href: "/developers/portal",
      cta: "Open portal",
      accent: "var(--color-denim)",
    },
  },
  {
    label: "Company",
    href: "/company",
    icon: "Building2",
    columns: [
      {
        title: "Hub + Sitemap",
        items: [
          { label: "Company Hub", href: "/company", icon: "LayoutGrid", description: "All company pages" },
          { label: "Complete Sitemap", href: "/sitemap", icon: "Map", description: "Every page on the site" },
          { label: "About CryptoMize", href: "/about", icon: "Building2", description: "The organization" },
          { label: "Our Story", href: "/history", icon: "BookOpen", description: "From vision to platform" },
          { label: "Mission", href: "/mission", icon: "Compass", description: "What drives us" },
          { label: "Leadership", href: "/leadership", icon: "Trophy", description: "The team" },
        ],
      },
      {
        title: "Join & Engage",
        items: [
          { label: "Careers", href: "/careers", icon: "Users", description: "Build what others can't" },
          { label: "Partners", href: "/partners", icon: "Handshake", description: "Partner ecosystem" },
          { label: "Press", href: "/press", icon: "Newspaper", description: "Media resources" },
          { label: "Demo", href: "/demo", icon: "PlayCircle", description: "Request a demonstration" },
          { label: "Contact", href: "/contact", icon: "Mail", description: "Get in touch" },
          { label: "Support", href: "/support", icon: "LifeBuoy", description: "Help center" },
          { label: "Status", href: "/status", icon: "Activity", description: "Platform uptime" },
        ],
      },
      {
        title: "Legal",
        items: [
          { label: "Legal Hub", href: "/legal", icon: "LayoutGrid", description: "All legal pages" },
          { label: "Privacy Policy", href: "/privacy", icon: "Shield", description: "Data protection" },
          { label: "Terms of Service", href: "/terms", icon: "FileText", description: "Usage terms" },
          { label: "Compliance", href: "/compliance", icon: "BadgeCheck", description: "Regulatory" },
          { label: "Security", href: "/security", icon: "Lock", description: "Security posture" },
          { label: "Cookies", href: "/cookies", icon: "Cookie", description: "Cookie policy" },
          { label: "Data Processing", href: "/data-processing", icon: "Database", description: "DPA" },
          { label: "SLA", href: "/sla", icon: "Award", description: "Service-level agreement" },
          { label: "Acceptable Use", href: "/acceptable-use", icon: "BookCheck", description: "Usage policy" },
        ],
      },
    ],
    featured: {
      title: "About CryptoMize",
      description: "15+ years. 300+ elite clients. Nine proprietary AI engines. Zero security incidents.",
      href: "/about",
      cta: "Learn about us",
      accent: "var(--color-violet)",
    },
  },
];
